'use client';

import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { cn } from '@/app/utils/cn';
import useCollection from '@/hooks/useCollection';
import { usePathname } from 'next/navigation';
import { adapterBlogSlug } from '@/app/utils/blogAdapter';

import { ArrowDownIcon, SearchIcon } from './icons';
import ListingContentlets from './listingContentlets';
import CategoryButton from './categoryButton';
import { normalizeTagList } from '@/app/utils/tags';
import LoadingPosts from '@/components/shared/LoadingPosts';
import NoResults from '@/components/shared/NoResults';
import FilterLabel from '@/components/shared/FilterLabel';

function buildParsedTagsFromContentlets(contentlets) {
    const tagSet = new Map();

    (contentlets || []).forEach((c) => {
        const list = normalizeTagList(c?.tags);
        list.forEach((displayTag) => {
            const key = String(displayTag)
                .toLowerCase()
                .replace(/^api(s)?$/i, (_, s) => `API${s ? 's' : ''}`)
                .replace(/^dotcms features$/i, 'dotCMS Features')
                .replace(/^dotcms$/i, 'dotCMS');

            tagSet.set(key.toLowerCase(), {
                key: key,
                categoryName: displayTag
            });
        });
    });

    return Array.from(tagSet.values()).sort((a, b) =>
        (a.categoryName || '').localeCompare(b.categoryName || '')
    );
}

export default function ContentletFilter({
    Card,
    limit = 9,
    contentType,
    queryConfig,
    categories = [],
    initialCollection,
    hideFilter,
    hideSearchBar,
    mosaicStyle,
    showCategoryFromSlug,
    showTopMargin,
    showAuthor,
    centerPositionCategoryFilter = false,
    showTagsFilter,
    showContributorFilter,
    // filterContributorByCompany,
    conhostId,
    autoPriority,
    deviceType = 'desktop',
    contributors
}) {
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const tag = searchParams.get('tag');
    const categoryFromUrl = searchParams.get('category');

    const initialFilter = useMemo(() => {
        const filter = {};
        const { list = [], field } = categories;

        if (categoryFromUrl && list.length > 0) {
            const foundCategory = list.find((item) => categoryFromUrl === item.categoryName);
            if (foundCategory && foundCategory.key !== '*') {
                filter.category = {
                    value: foundCategory.key,
                    field
                };
            }
        }

        if (showCategoryFromSlug && !filter.category && pathname) {
            const categoryFromSlug = pathname.split('/').filter(Boolean).pop() || '';
            const adapted = adapterBlogSlug(categoryFromSlug);
            if (adapted && adapted !== '*') {
                filter.category = {
                    value: adapted,
                    field: categories.field || 'categories'
                };
            }
        }

        return filter;
    }, [categoryFromUrl, pathname, showCategoryFromSlug, categories]);

    const { collection, setFilter, setPage, setSearch, isLoading } = useCollection({
        limit,
        search: '',
        tag,
        filter: initialFilter,
        contentType,
        queryConfig,
        initialCollection,
        conhostId
    });

    const loaderRef = useRef(null);
    const isCenter = centerPositionCategoryFilter ? `mx-auto justify-center` : '';

    const getFilterContainerClassName = (hideFilter, showTagsFilter, showContributorFilter) =>
        `w-full justify-between gap-x-4 md:py-6 ${hideFilter && !showTagsFilter && !showContributorFilter ? 'hidden' : 'flex min-h-12 '}`;

    const safeContentlets = collection?.contentlets ?? [];
    const safeTotal = collection?.total ?? 0;

    // const [contributors, setContributors] = useState([]);
    const [activeContributors, setActiveContributors] = useState([]);
    const [activeTags, setActiveTags] = useState([]);

    const handleObserver = useCallback(
        (entries) => {
            const target = entries[0];
            if (target.isIntersecting && safeTotal > safeContentlets.length) {
                setPage((prevPage) => prevPage + 1);
            }
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [safeContentlets.length, safeTotal]
    );

    useEffect(() => {
        const option = {
            root: null,
            rootMargin: '20px',
            threshold: 0.1
        };
        const observer = new IntersectionObserver(handleObserver, option);
        const currentLoaderRef = loaderRef.current;

        if (currentLoaderRef) observer.observe(currentLoaderRef);

        return () => {
            if (currentLoaderRef) observer.unobserve(currentLoaderRef);
        };
    }, [handleObserver]); // eslint-disable-line react-hooks/exhaustive-deps

    // useEffect(() => {
    //     const run = async () => {
    //         if (!showContributorFilter) return;
    //         const list = await fetchContributorsUtil({ companyId: filterContributorByCompany });
    //         setContributors(list);
    //     };

    //     run();
    // }, [showContributorFilter, filterContributorByCompany]);

    const setSelectedContributors = (contributorIds) => {
        setFilter((prev) => ({
            ...prev,
            contributor: {
                value: contributorIds.length === 0 ? null : contributorIds,
                field: 'author'
            }
        }));
        setActiveContributors(contributorIds);
    };

    const mapActiveContributorsToSelectedOptions = (activeContributors, contributors) => {
        return activeContributors
            .map((contributorId) => {
                const contributor = contributors.find(
                    (c) => c.identifier === contributorId || c.inode === contributorId
                );
                return contributor
                    ? {
                          value: contributorId,
                          label: `${contributor.firstName} ${contributor.lastName}`
                      }
                    : null;
            })
            .filter(Boolean);
    };

    const initialContentlets = initialCollection?.contentlets || [];
    const parsedTags = buildParsedTagsFromContentlets([...initialContentlets, ...safeContentlets]);

    return (
        <>
            <div
                data-component="contentlet-filter"
                className={cn(
                    getFilterContainerClassName(hideFilter, showTagsFilter, showContributorFilter)
                )}>
                <div className={`flex-wrap gap-4 md:flex ${isCenter}`}>
                    <CategoriesButtons
                        hideFilter={hideFilter}
                        showCategoryFromSlug={showCategoryFromSlug}
                        categories={categories}
                        setFilter={setFilter}
                        initialFilter={initialFilter}
                    />

                    {showTagsFilter && (
                        <div className="z-5 relative flex flex-col">
                            <FilterLabel>Topics</FilterLabel>
                            <CategoryButton
                                multiselect={true}
                                enableSmartSearch={true}
                                searchPlaceholder="Search topics..."
                                options={parsedTags.map((tag) => ({
                                    value: tag.key,
                                    label: tag.categoryName
                                }))}
                                selectedOptions={activeTags.map((tag) => ({
                                    value: tag,
                                    label:
                                        parsedTags.find((t) => t.key === tag)?.categoryName || tag
                                }))}
                                onSelectionChange={(selectedTags) => {
                                    const tagsValues = selectedTags.map((t) => t.value);
                                    const tagLabels = selectedTags.map((t) => t.label);
                                    setActiveTags(tagsValues);
                                    setFilter((prev) => ({
                                        ...prev,
                                        tags: {
                                            value: tagLabels.length === 0 ? null : tagLabels,
                                            field: 'tags'
                                        }
                                    }));
                                }}
                                defaultText="All topics"
                                maxDisplayItems={2}
                                className="flex min-w-[295px] items-center justify-center gap-2 bg-fuschia-700 px-[12px] text-base text-white">
                                All topics
                            </CategoryButton>
                        </div>
                    )}

                    {showContributorFilter && (
                        <div className="relative z-10 mt-2 flex flex-col md:mt-0">
                            <FilterLabel>Authors</FilterLabel>
                            <CategoryButton
                                multiselect={true}
                                enableSmartSearch={true}
                                searchPlaceholder="Search contributors..."
                                options={contributors.map((contributor) => ({
                                    value: contributor?.identifier || contributor?.inode,
                                    label: `${(contributor?.firstName || '').trim()} ${(contributor?.lastName || '').trim()}`.trim()
                                }))}
                                selectedOptions={mapActiveContributorsToSelectedOptions(
                                    activeContributors,
                                    contributors
                                )}
                                onSelectionChange={(selectedContributors) => {
                                    const contributorIds = selectedContributors.map((c) => c.value);
                                    setSelectedContributors(contributorIds);
                                }}
                                defaultText="All contributors"
                                maxDisplayItems={2}
                                className="flex min-w-[295px] items-center justify-center gap-2 bg-fuschia-700 px-[12px] text-base text-white"
                            />
                        </div>
                    )}

                    {(showTagsFilter || showContributorFilter) &&
                        (activeTags.length > 0 || activeContributors.length > 0) && (
                            <div className="mt-6 flex items-end md:mt-0">
                                <CategoryButton
                                    className="z-1 relative w-full max-w-full !py-[9px] px-2 text-base hover:!bg-fuschia-200 hover:text-black md:max-w-[225px] md:px-10"
                                    onClick={() => {
                                        setActiveTags([]);
                                        setSelectedContributors([]);
                                        setFilter((prev) => ({
                                            ...prev,
                                            tags: { value: null, field: 'tags' }
                                        }));
                                    }}>
                                    Clear All
                                </CategoryButton>
                            </div>
                        )}
                </div>
                {!hideSearchBar && (
                    <div className="relative flex h-12 w-full items-center justify-center text-white outline-none md:w-[382px]">
                        {!showCategoryFromSlug && (
                            <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
                                <SearchIcon />
                            </div>
                        )}
                        <input
                            type="text"
                            onChange={(e) => setSearch(e.target.value)}
                            className={cn(
                                `${showCategoryFromSlug && 'invisible'} block size-full rounded-lg bg-blue-100 p-3 ps-11 text-sm text-blue-600 outline-none placeholder:text-blue-600`
                            )}
                            placeholder="Search in Insights"
                        />
                    </div>
                )}
            </div>

            {safeContentlets.length > 0 ? (
                <ListingContentlets
                    maxLimit={limit}
                    autoPriority={autoPriority}
                    mosaicStyle={mosaicStyle}
                    Card={Card}
                    showTopMargin={!!showTopMargin}
                    showAuthor={!!showAuthor}
                    contentType={contentType}
                    contentlets={safeContentlets}
                    deviceType={deviceType}
                />
            ) : !isLoading ? (
                <NoResults className="mt-3 w-full" />
            ) : null}

            {isLoading && <LoadingPosts className="mt-3" />}

            {safeTotal > safeContentlets.length && (
                <div className="mt-16 flex items-center justify-center">
                    <div
                        ref={loaderRef}
                        className="px-6 py-4 text-base font-semibold text-blue-600">
                        {safeTotal > safeContentlets.length && <span></span>}
                    </div>
                </div>
            )}
        </>
    );
}

const CategoriesButtons = ({
    hideFilter,
    categories,
    setFilter,
    showCategoryFromSlug,
    initialFilter = {}
}) => {
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const search = searchParams.get('category');

    const initialActive = initialFilter?.category?.value || '*';
    const [active, setActive] = useState(initialActive);

    const { list = [], field } = categories;
    const allCategory = { key: '*', categoryName: 'All' };

    const mainCategories = [allCategory, ...list.slice(0, 3)];
    const moreCategories = list.slice(3);

    const [showMore, setShowMore] = useState(false);
    const dropdownRef = useRef(null);
    const hasInitializedRef = useRef(false);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowMore(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownRef]);

    const setCategory = useCallback(
        (value) => {
            setFilter((prev) => {
                const newFilter = {
                    ...prev,
                    category: {
                        value,
                        field
                    }
                };
                return newFilter;
            });
            setActive(value);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setFilter, field, active]
    );

    useEffect(() => {
        if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;
            if (initialFilter?.category?.value) {
                if (initialFilter.category.value !== active) {
                    setActive(initialFilter.category.value);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!hasInitializedRef.current) {
            return;
        }

        if (search) {
            const found = list.find((item) => search === item.categoryName);
            const key = found?.key || '*';
            if (key !== active) {
                setCategory(key);
            }
        } else if (!search && active !== '*') {
            if (!showCategoryFromSlug || initialActive === '*') {
                setCategory('*');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, list]);

    useEffect(() => {
        if (!hasInitializedRef.current) {
            return;
        }

        const categoryFromSlug = pathname ? pathname.split('/').filter(Boolean).pop() : '';
        const adapted = adapterBlogSlug(categoryFromSlug || '');

        if (showCategoryFromSlug) {
            const value = adapted || '*';
            if (value !== active) {
                setCategory(value);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, showCategoryFromSlug]);

    if (hideFilter) return null;

    return (
        <>
            {mainCategories.map(({ key, categoryName }) => (
                <CategoryButton key={key} active={active === key} onClick={() => setCategory(key)}>
                    {categoryName}
                </CategoryButton>
            ))}
            {!!moreCategories.length && (
                <div className="relative z-10 flex" ref={dropdownRef}>
                    <CategoryButton onClick={() => setShowMore((prev) => !prev)}>
                        More <ArrowDownIcon />
                    </CategoryButton>
                    <div
                        className={cn(
                            `absolute top-12 z-50 flex max-h-80 w-52 flex-col gap-5 overflow-y-auto bg-white px-5 py-4 shadow-2xl ${
                                !showMore && 'hidden'
                            }`
                        )}>
                        {moreCategories.map(({ key, categoryName }) => (
                            <CategoryButton
                                key={key}
                                active={active === key}
                                onClick={() => setCategory(key)}>
                                {categoryName}
                            </CategoryButton>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};
